var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { accountBadgeDistributionStatuses, distributionStatusHistoryActionMap, getAccountBadgeValidDistributionStatusChanges, logBadgeHistoryActionFunctionDef, } from '../../../../backend/features/badges/shared';
import { useAccountBadgeActionChecks, } from '../../../../ui/features/badge-management/hooks/useAccountBadgeActionChecks';
import { useAccountBadgeReissueAction } from '../../../../ui/features/badge-management/hooks/useAccountBadgeReissueAction';
import { useUpdateAccountBadgeWithHistory } from '../../../../ui/features/badge-management/hooks/useUpdateAccountBadgeWithHistory';
import { useBadgingTranslation } from '../../../../ui/hooks';
import { showNotificationDialog } from '@juvare/alert-ui/ui/features/alerts';
import { showFormDialog } from '@juvare/common-form-ui/showFormDialog';
import { handleErrorSnackbar, showAlertSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { useTypedPlatformFunction } from '@juvare/platform-sdk-react/ui/features/typed-platform-functions';
import { startOfDay } from 'date-fns/startOfDay';
import { useAccountBadgeHistoryOpenAction } from './useAccountBadgeHistoryOpenAction';
import { useAccountBadgeRevokeAction } from './useAccountBadgeRevokeAction';
const defaultExpirationTimeIso = '23:59:59.999';
/**
 * Centralized place for all single Account Badge actions
 */
export function useAccountBadgeActions(params) {
    const { accountBadge, onActionSuccess } = params;
    const { t } = useBadgingTranslation();
    const checks = useAccountBadgeActionChecks({ accountBadgesSelected: accountBadge ? [accountBadge] : [] });
    const { handleAccountBadgeRevoke } = useAccountBadgeRevokeAction(params);
    const { handleAccountBadgeReissue } = useAccountBadgeReissueAction(params);
    const { handleAccountBadgeHistoryOpen } = useAccountBadgeHistoryOpenAction(params);
    const logBadgeHistoryAction = useTypedPlatformFunction(logBadgeHistoryActionFunctionDef);
    const { updateAccountWithHistory } = useUpdateAccountBadgeWithHistory(accountBadge === null || accountBadge === void 0 ? void 0 : accountBadge.columns.badgeId);
    const handleRequestSuccess = (options, actionOptions) => {
        var _a;
        const onSuccessFn = (_a = actionOptions === null || actionOptions === void 0 ? void 0 : actionOptions.onActionSuccess) !== null && _a !== void 0 ? _a : onActionSuccess;
        void onSuccessFn();
        showAlertSnackbar({
            horizontalPosition: 'center',
            type: 'success',
            verticalPosition: 'bottom',
            autoHideDuration: 2000,
            message: options.message,
        });
    };
    const updateAccountBadge = (data, historyAction, actionOptions) => __awaiter(this, void 0, void 0, function* () {
        if (!accountBadge) {
            return;
        }
        return updateAccountWithHistory(accountBadge.id, data, { action: historyAction })
            .then(() => handleRequestSuccess({ message: t('badges.badgeList.editDialog.successMessage') }, actionOptions))
            .catch(handleErrorSnackbar);
    });
    const handleAccountBadgeDistributionStatusEdit = (actionOptions) => {
        if (!accountBadge) {
            return;
        }
        const status = accountBadge.columns.status;
        const distributionStatus = accountBadge.columns.distributionStatus;
        const allowedDistributionStatuses = [
            ...getAccountBadgeValidDistributionStatusChanges({
                status,
                distributionStatus,
            }).allowed,
            distributionStatus,
        ].sort((a, b) => accountBadgeDistributionStatuses.indexOf(a) - accountBadgeDistributionStatuses.indexOf(b));
        showFormDialog({
            title: t('badges.badgeDetails.accountBadgeTable.menuItems.changeDistributionStatus.modalTitle', {
                name: accountBadge.columns.accountDisplayName,
            }),
            config: {
                type: 'form.advancedRadio',
                key: 'distributionStatus',
                validate: { required: true },
                values: allowedDistributionStatuses.map((accountBadgeDistributionStatus) => ({
                    value: accountBadgeDistributionStatus,
                    label: t(`badges.badgeDetails.distributionStatuses.${accountBadgeDistributionStatus}`),
                    description: t(`badges.badgeDetails.distributionStatusDescriptions.${accountBadgeDistributionStatus}`),
                })),
            },
            data: { distributionStatus },
            submitLabel: t('common.save'),
            onSubmit: ({ distributionStatus }) => {
                void updateAccountBadge({ distributionStatus }, distributionStatusHistoryActionMap[distributionStatus], actionOptions);
            },
        });
    };
    const handleAccountBadgeExpirationDateEdit = (actionOptions) => {
        if (!accountBadge) {
            return;
        }
        const expiresAtDate = new Date(accountBadge.columns.expiresAt);
        showFormDialog({
            title: t('badges.badgeDetails.accountBadgeTable.menuItems.changeExpirationDate.modalTitle', {
                name: accountBadge.columns.accountDisplayName,
            }),
            config: {
                type: 'form.datePicker',
                label: t('badges.badgeDetails.accountBadgeTable.menuItems.changeExpirationDate.expirationDate'),
                key: 'expiresAt',
                format: 'mm/dd/yyyy',
                validate: { required: true, minDate: startOfDay(new Date()).toISOString() },
            },
            data: {
                expiresAt: expiresAtDate.toISOString().split('T')[0],
            },
            onSubmit: ({ expiresAt }) => {
                return updateAccountBadge({ expiresAt: Date.parse(`${expiresAt}T${defaultExpirationTimeIso}Z`) }, 'accountBadgeExpirationDateChanged', actionOptions);
            },
        });
    };
    const handleAccountBadgeNotificationSend = () => {
        if (!accountBadge) {
            return;
        }
        showNotificationDialog({
            onSubmitSuccess: (_, onClose) => {
                showAlertSnackbar({
                    message: t('badges.badgeDetails.accountBadgeTable.notification.sent'),
                    type: 'success',
                    autoHideDuration: 2000,
                });
                onClose();
                logBadgeHistoryAction({
                    accountBadgeIds: [accountBadge.id],
                    badgeId: accountBadge.columns.badgeId,
                    action: 'accountBadgeNotificationSent',
                }).catch(handleErrorSnackbar);
            },
            mode: 'notification',
            reviewFormHeader: t('badges.badgeDetails.accountBadgeTable.notification.reviewAndSend'),
            wizardHeader: t('badges.badgeDetails.accountBadgeTable.notification.createNotification'),
            partialAlert: {
                recipients: {
                    internal: {
                        includeNonMembers: false,
                        accounts: [accountBadge.columns.accountId],
                        groups: [],
                        organizations: [],
                    },
                },
            },
        });
    };
    const handleAccountBadgeDistributionStatusChange = (distributionStatus, actionOptions) => {
        void updateAccountBadge({ distributionStatus }, distributionStatusHistoryActionMap[distributionStatus], actionOptions);
    };
    return Object.assign(Object.assign({}, checks), { handleAccountBadgeDistributionStatusEdit,
        handleAccountBadgeExpirationDateEdit,
        handleAccountBadgeNotificationSend,
        handleAccountBadgeDistributionStatusChange,
        handleAccountBadgeHistoryOpen,
        handleAccountBadgeRevoke,
        handleAccountBadgeReissue });
}
